import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  AlertTriangle, CheckCircle, MessageSquare, Search, 
  Filter, RefreshCw, TrendingDown, Edit3, Check, X 
} from 'lucide-react'
import { api } from '../../lib/api'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface FeedbackItem {
  id: string
  chunk_id: string
  document_id: string
  user_id: string
  feedback_type: 'doubt' | 'confirm' | 'correction'
  original_content: string
  suggested_correction?: string
  reason?: string
  status: string
  created_at: string
  updated_at: string
}

interface BulkUpdateStatus {
  id: string
  update_type: string
  old_value: string
  new_value: string
  affected_documents: string[]
  affected_chunks: string[]
  status: string
  changes_count: number
  created_at: string
  completed_at?: string
  error_message?: string
}

export function KnowledgeConfidence() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'feedback' | 'updates' | 'low-confidence'>('feedback')
  const [filterStatus, setFilterStatus] = useState<string>('pending')
  const [searchTerm, setSearchTerm] = useState('')
  const [bulkUpdateForm, setBulkUpdateForm] = useState({
    old_value: '',
    new_value: '',
    update_type: 'correction'
  })

  // Fetch pending feedback
  const { data: feedbackData, isLoading: feedbackLoading, refetch: refetchFeedback } = useQuery(
    ['pending-feedback', filterStatus],
    async () => {
      const response = await api.get('/api/knowledge-confidence/feedback/pending')
      return response.data as FeedbackItem[]
    },
    { enabled: activeTab === 'feedback' }
  )

  // Fetch knowledge updates
  const { data: updatesData, isLoading: updatesLoading } = useQuery(
    ['knowledge-updates'],
    async () => {
      const response = await api.get('/api/knowledge-confidence/updates')
      return response.data as BulkUpdateStatus[]
    },
    { enabled: activeTab === 'updates' }
  )

  // Fetch low confidence content
  const { data: lowConfidenceData, isLoading: lowConfidenceLoading } = useQuery(
    ['low-confidence'],
    async () => {
      const response = await api.get('/api/knowledge-confidence/low-confidence?threshold=0.5')
      return response.data
    },
    { enabled: activeTab === 'low-confidence' }
  )

  // Review feedback mutation
  const reviewMutation = useMutation(
    async ({ feedbackId, status, notes }: { feedbackId: string; status: string; notes?: string }) => {
      return api.patch(`/api/knowledge-confidence/feedback/${feedbackId}/review`, null, {
        params: { status, review_notes: notes }
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pending-feedback'])
        refetchFeedback()
      }
    }
  )

  // Bulk update mutation
  const bulkUpdateMutation = useMutation(
    async (data: typeof bulkUpdateForm) => {
      return api.post('/api/knowledge-confidence/bulk-update', data)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['knowledge-updates'])
        setBulkUpdateForm({ old_value: '', new_value: '', update_type: 'correction' })
      }
    }
  )

  const handleReviewFeedback = (feedbackId: string, status: string, notes?: string) => {
    reviewMutation.mutate({ feedbackId, status, notes })
  }

  const handleBulkUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (bulkUpdateForm.old_value && bulkUpdateForm.new_value) {
      bulkUpdateMutation.mutate(bulkUpdateForm)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-yellow-500" />
          Wissensqualität & Feedback
        </h2>
        
        <button
          onClick={() => refetchFeedback()}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          title="Aktualisieren"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-white/10">
        <button
          onClick={() => setActiveTab('feedback')}
          className={`pb-3 px-1 transition-colors relative ${
            activeTab === 'feedback' 
              ? 'text-primary-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Feedback & Zweifel
          {activeTab === 'feedback' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400" />
          )}
        </button>
        
        <button
          onClick={() => setActiveTab('updates')}
          className={`pb-3 px-1 transition-colors relative ${
            activeTab === 'updates' 
              ? 'text-primary-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Massen-Updates
          {activeTab === 'updates' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400" />
          )}
        </button>
        
        <button
          onClick={() => setActiveTab('low-confidence')}
          className={`pb-3 px-1 transition-colors relative ${
            activeTab === 'low-confidence' 
              ? 'text-primary-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Niedrige Vertrauenswürdigkeit
          {activeTab === 'low-confidence' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400" />
          )}
        </button>
      </div>

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <div className="space-y-4">
          {feedbackLoading ? (
            <div className="text-center py-8">Lade Feedback...</div>
          ) : feedbackData?.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Kein offenes Feedback vorhanden</p>
            </div>
          ) : (
            feedbackData?.map((feedback) => (
              <div key={feedback.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {feedback.feedback_type === 'doubt' && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                          Zweifel
                        </span>
                      )}
                      {feedback.feedback_type === 'correction' && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                          Korrektur
                        </span>
                      )}
                      {feedback.feedback_type === 'confirm' && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                          Bestätigung
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {format(new Date(feedback.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm text-gray-400 mb-1">Original:</p>
                      <p className="text-sm bg-black/20 p-2 rounded">
                        {feedback.original_content.substring(0, 200)}...
                      </p>
                    </div>
                    
                    {feedback.suggested_correction && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-400 mb-1">Korrekturvorschlag:</p>
                        <p className="text-sm bg-green-500/10 p-2 rounded text-green-400">
                          {feedback.suggested_correction}
                        </p>
                      </div>
                    )}
                    
                    {feedback.reason && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-400 mb-1">Begründung:</p>
                        <p className="text-sm">{feedback.reason}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleReviewFeedback(feedback.id, 'approved')}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded text-green-400 transition-colors"
                      title="Genehmigen"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReviewFeedback(feedback.id, 'rejected')}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 transition-colors"
                      title="Ablehnen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {feedback.feedback_type === 'correction' && (
                      <button
                        onClick={() => handleReviewFeedback(feedback.id, 'implemented')}
                        className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400 transition-colors"
                        title="Implementieren"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Bulk Updates Tab */}
      {activeTab === 'updates' && (
        <div className="space-y-6">
          {/* Bulk Update Form */}
          <form onSubmit={handleBulkUpdate} className="p-4 bg-white/5 rounded-lg border border-white/10">
            <h3 className="font-medium mb-4">Neues Massen-Update</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm mb-2">Alter Wert:</label>
                <input
                  type="text"
                  value={bulkUpdateForm.old_value}
                  onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, old_value: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded focus:outline-none focus:border-primary-500"
                  placeholder="z.B. Reisekosten: 95€"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-2">Neuer Wert:</label>
                <input
                  type="text"
                  value={bulkUpdateForm.new_value}
                  onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, new_value: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded focus:outline-none focus:border-primary-500"
                  placeholder="z.B. Reisekosten: 99€"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={bulkUpdateMutation.isLoading || !bulkUpdateForm.old_value || !bulkUpdateForm.new_value}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 rounded transition-colors"
            >
              {bulkUpdateMutation.isLoading ? 'Wird verarbeitet...' : 'Update starten'}
            </button>
          </form>

          {/* Update History */}
          <div className="space-y-3">
            <h3 className="font-medium">Update-Verlauf</h3>
            {updatesLoading ? (
              <div className="text-center py-4">Lade Updates...</div>
            ) : updatesData?.length === 0 ? (
              <div className="text-center py-4 text-gray-400">Keine Updates vorhanden</div>
            ) : (
              updatesData?.map((update) => (
                <div key={update.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      update.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      update.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      update.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {update.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(update.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </span>
                  </div>
                  
                  <div className="text-sm">
                    <p className="mb-1">
                      <span className="text-gray-400">Von:</span> {update.old_value}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-400">Nach:</span> {update.new_value}
                    </p>
                    <p className="text-gray-400">
                      {update.changes_count} Änderungen in {update.affected_documents.length} Dokumenten
                    </p>
                  </div>
                  
                  {update.error_message && (
                    <p className="mt-2 text-sm text-red-400">{update.error_message}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Low Confidence Tab */}
      {activeTab === 'low-confidence' && (
        <div className="space-y-4">
          {lowConfidenceLoading ? (
            <div className="text-center py-8">Lade Inhalte...</div>
          ) : lowConfidenceData?.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Alle Inhalte haben hohe Vertrauenswürdigkeit</p>
            </div>
          ) : (
            lowConfidenceData?.map((item: any) => (
              <div key={item.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-medium">
                      Vertrauen: {Math.round(item.confidence_score * 100)}%
                    </span>
                    {item.auto_flagged && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                        Auto-markiert
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {item.doubt_count} Zweifel, {item.confirm_count} Bestätigungen
                  </span>
                </div>
                
                {item.document_chunks && (
                  <div className="text-sm">
                    <p className="text-gray-400 mb-1">
                      Dokument: {item.document_chunks.documents?.title || 'Unbekannt'}
                    </p>
                    <p className="bg-black/20 p-2 rounded">
                      {item.document_chunks.content?.substring(0, 200)}...
                    </p>
                  </div>
                )}
                
                {item.auto_flag_reason && (
                  <p className="mt-2 text-sm text-red-400">{item.auto_flag_reason}</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </motion.div>
  )
}